import { NextRequest, NextResponse } from 'next/server';
import { db } from '@/lib/db';
import { verifyPassword, generateToken } from '@/lib/auth';

export async function POST(request: NextRequest) {
  try {
    const body = await request.json();
    const { email, password } = body;

    // Validation
    if (!email || !password) {
      return NextResponse.json(
        { error: 'Email and password are required' },
        { status: 400 }
      );
    }

    // Find user by email
    const user = await db.users.findByEmail(email);
    if (!user) {
      return NextResponse.json(
        { error: 'Invalid email or password' },
        { status: 401 }
      );
    }

    // Verify password
    const isValidPassword = await verifyPassword(password, user.passwordHash);
    if (!isValidPassword) {
      return NextResponse.json(
        { error: 'Invalid email or password' },
        { status: 401 }
      );
    }

    // Check user status
    if (user.status === 'suspended') {
      return NextResponse.json(
        { error: 'Your account has been suspended. Please contact support.' },
        { status: 403 }
      );
    }

    if (user.status === 'deleted') {
      return NextResponse.json(
        { error: 'Your account has been deleted. Please contact support.' },
        { status: 403 }
      );
    }

    // Check if user is admin - admins must use admin login page
    if (user.role === 'admin') {
      return NextResponse.json(
        { error: 'Invalid credentials' },
        { status: 401 }
      );
    }

    // Check if user is a gym owner
    const gymProfiles = await db.gyms.findByOwnerEmail(email);
    const isGymOwner = gymProfiles.length > 0;

    // Check if user is a trainer
    const trainerProfiles = await db.trainers.findByOwnerEmail(email);
    const isTrainer = trainerProfiles.length > 0;

    // Determine user role
    let role = 'user'; // default
    if (isGymOwner) role = 'gym_owner';
    else if (isTrainer) role = 'trainer';

    // Generate JWT token
    const token = await generateToken({
      userId: user.id,
      email: user.email,
      role: role,
    });

    // Return success response
    return NextResponse.json(
      {
        success: true,
        message: 'Login successful',
        token,
        user: {
          id: user.id,
          fullName: user.fullName,
          email: user.email,
          phone: user.phone,
          profilePicture: user.profilePicture || null,
          role: role,
        },
      },
      { status: 200 }
    );
  } catch (error) {
    console.error('Login error:', error);
    return NextResponse.json(
      { error: 'Internal server error' },
      { status: 500 }
    );
  }
}
